import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/admin-layout';
import CMHeader from '../../PageComponentData/Common/Pageheader';
import { ReferralConsultationHistory } from "../../apis";
import Placeholder from '../../components/placeholder';
import NoConsultation from '../../lottiefiles/no_consultation.json';
import loader from '../../lottiefiles/loader.json';
import { FlagIcon } from "@heroicons/react/24/solid";
import { IoIosArrowBack } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";

const formatINR = n => new Intl.NumberFormat('en-IN').format(Number(n || 0));
const ordinal = n => (n === 1 ? 'First' : n === 2 ? 'Second' : n === 3 ? 'Third' : `${n}th`);

const getTargetMilestones = (list) => {
  if (!Array.isArray(list) || list.length === 0) return 0;
  const last = list[list.length - 1];
  return Number(last?.range ?? 0);
};

const normalizeIds = (it) => {
  const raw = Array.isArray(it?.transactionIds)
    ? it.transactionIds
    : it?.transactionId
      ? [it.transactionId]
      : [];
  return Array.from(
    new Set(
      raw
        .flat(Infinity)
        .map(x => (typeof x === 'string' ? x.trim() : String(x ?? '').trim()))
        .filter(Boolean)
    )
  );
};

const MilestoneMobComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const item = location.state?.item;
    const [imgErrors, setImgErrors] = useState({}); // Track image errors by item._id
   const handleImgError = (id) => {
      setImgErrors((prev) => ({ ...prev, [id]: true }));
    };
const firstLetter = item.DoctorName ? item.DoctorName.charAt(0).toUpperCase() : '';
          const isImgError = imgErrors[item._id];

  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // target milestones from last .range on the clicked item
  const targetMilestones = useMemo(
    () => getTargetMilestones(item?.referraltransaction),
    [item]
  );

  useEffect(() => {
    if (!item) {
      // no context; go back
      navigate(-1);
      return;
    }

    let mounted = true;

    (async () => {
      setIsLoading(true);
      try {
        const ids = normalizeIds(item);
        if (ids.length === 0) {
          if (mounted) {
            setGroups([]);
            setExpanded({});
          }
        } else {
          const response = await ReferralConsultationHistory({ transactionIds: ids });
          const results = response?.data?.data?.results ?? [];

          const sorted = results ?? [];



          if (mounted) {
            setGroups(sorted);
            const firstTxId = sorted[0]?.transactionId;
            setExpanded(firstTxId ? { [firstTxId]: true } : {});
          }
        }
      } catch (e) {
        console.log('Failed to fetch referral consultation history:', e);
        if (mounted) {
          setGroups([]);
          setExpanded({});
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [item, navigate]);

  const toggle = (tx) => setExpanded(prev => ({ ...prev, [tx]: !prev[tx] }));

  return (
    <AdminLayout headerTxt={'Referral Management'} >
      <CMHeader headertxt='Milestone Completed History' backbtn={true}/>

      <div className="p-0 md:p-4">
        <section className="py-5 p-4 rounded-2xl gap-x-4 bg-transparent md:bg-white" style={{ width:'100%',marginBottom:"60px",}}>
          {/* Top bar for mobile */}
          <div className="flex items-center gap-3 mb-4">
           
            
            <div
              className="rh-item"
              key={item._id}
              // onClick={() => openModal(item)}
                  //  onClick={() => handleClick(item)}
              style={{ width:'100%',marginBottom:"0px"}}
            >
              {!isImgError && item.coverPic ? (
                <img
                  src={`${process.env.REACT_APP_API_URL}/${item.coverPic}`}
                  alt={item.DoctorName}
                  className="rh-avatar"
                  onError={() => handleImgError(item._id)}
                />
              ) : (
                <div className="rh-avatar fallback-avatar">
                  {firstLetter}
                </div>
              )}
              <p className="rh-name">{item.DoctorName || ''}</p>
              <span className="rh-amount">₹ {item.referredTotal || 0}</span>
            </div>
          </div>

          
          {isLoading ? (
            <div className="bg-white rounded-lg pt-4 pb-4 flex items-center justify-center">
              <Placeholder title="" animationData={loader} message="" />
            </div>
          ) : groups.length === 0 ? (
            <div className="bg-white rounded-lg pt-4 pb-4 flex items-center justify-center">
              <Placeholder
                title="No Consultation History found."
                animationData={NoConsultation}
                message="No Consultation Transaction History is found, complete Consultations to earn."
              />
            </div>
          ) : (
            <div className="space-y-4 bg-white">
              {groups.map(group => {
                const tx = group?.transactionId || '';
                const total = Number(group?.referredtransactionTotal ?? group?.referraltransactionTotal ?? 0);
                const completed = Array.isArray(group.items) ? group.items.length : 0;

                return (
                  <div key={tx} className=" rounded-xl p-3">
                    <div
                      className="milestone-item-container flex items-center gap-3"
                      onClick={() => toggle(tx)}
                      style={{ cursor: 'pointer' ,marginBottom:"0px",}}
                      role="button"
                    >
                      <FlagIcon className="w-5 h-5 text-blue-500" />
                      <div className="flex-1">
                        <p className="milestone-text text-sm">
                          {`You have earned ₹ ${formatINR(total)} from your referral.`}
                        </p>

                        {/* progress line */}
                        {targetMilestones > 0 && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                              <span>Progress</span>
                              <span>{completed} / {targetMilestones}</span>
                            </div>
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500"
                                style={{ width: `${Math.min(100, (completed / targetMilestones) * 100)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <span className="amount-badge text-sm font-semibold whitespace-nowrap">
                        ₹ {formatINR(total)}
                      </span>
                    </div>

                    {expanded[tx] && (
                      <div className="milestone-timeline mt-4">
                        {(group.items || []).map((milestone, index) => {
                          const label = `${ordinal(index + 1)} consultation completed`;
                          return (
                            <div
                              key={milestone?._id || `${tx}-${index}`}
                              className="milestone-item completed flex items-start gap-3 py-2"
                            >
                              <div className="milestone-marker w-2 h-2 rounded-full mt-2 bg-blue-500"></div>
                              <div className="milestone-content">
                                <p className="text-sm">{label}</p>
                                <p className="milestone-date text-xs text-gray-500">
                                  {new Date(milestone.createdAt).toLocaleString('en-IN', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true,
                                  })}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
};

export default MilestoneMobComponent;
